import { Col, Row, Space, Spin, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createCheckout,
  createCreditPackCheckout,
  getBillingPlans,
  getBillingQuota,
  getCreditHistory,
  getCreditPacks,
  getSubscription,
  previewBillingPlans,
  reconcilePaymentStatus,
  redeemSubscriptionCode,
} from '../api';
import CheckoutPaymentModal from '../components/CheckoutPaymentModal';
import { resolveErrorMessage } from '../utils/errorMessage';
import {
  formatPaymentAmount,
  resolveAnnualBasePriceCents,
  resolveMonthlyBasePriceCents,
} from '../utils/checkoutPricing';
import {
  buildCreditHistoryColumns,
  buildSubscriptionHistoryColumns,
} from './billing/historyColumns';
import {
  createCreditPackCheckoutFlow,
  createSubscriptionCheckoutFlow,
} from './billing/checkoutFlow';
import useSubscriptionHistory from './billing/useSubscriptionHistory';
import {
  TRIAL_PROMO_REASON_TEXT_MAP,
  QUOTA_TOOLTIP_MAP,
  formatCreditPackUnitPrice,
  formatDateTime,
  formatStorage,
  isPaymentApplied,
  planDurationDays,
  toNonNegativeInt,
  toUtcMs,
} from './billing/helpers';
import BillingSubscriptionSection from '../components/BillingSubscriptionSection';
import BillingPlanSection from '../components/BillingPlanSection';
import BillingCreditPackSection from '../components/BillingCreditPackSection';
import BillingCreditHistorySection from '../components/BillingCreditHistorySection';
import BillingHistoryDrawer from '../components/BillingHistoryDrawer';
import './Billing.css';

const HISTORY_PAGE_SIZE = 8;
const CREDIT_HISTORY_PAGE_SIZE = 6;

const Billing = () => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState({ latest: null, active: null });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionPlanId, setActionPlanId] = useState('');
  const [error, setError] = useState('');
  const [checkout, setCheckout] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const {
    historyOpen,
    historyLoading,
    historyError,
    historyItems,
    historyPage,
    historyTotal,
    loadSubscriptionHistory,
    openSubscriptionHistory,
    closeSubscriptionHistory,
  } = useSubscriptionHistory({ pageSize: HISTORY_PAGE_SIZE });
  const [quota, setQuota] = useState(null);
  const [creditPacks, setCreditPacks] = useState([]);
  const [creditPacksLoading, setCreditPacksLoading] = useState(false);
  const [creditPackEligibility, setCreditPackEligibility] = useState({
    eligible: false,
    reason: '',
    plan_id: '',
    plan_name: '',
  });
  const [creditPackActionLoading, setCreditPackActionLoading] = useState(false);
  const [creditPackActionPackId, setCreditPackActionPackId] = useState('');
  const [creditHistoryLoading, setCreditHistoryLoading] = useState(false);
  const [creditHistoryError, setCreditHistoryError] = useState('');
  const [creditHistoryItems, setCreditHistoryItems] = useState([]);
  const [creditHistoryPage, setCreditHistoryPage] = useState(1);
  const [creditHistoryTotal, setCreditHistoryTotal] = useState(0);
  const [trialPromoEligibility, setTrialPromoEligibility] = useState(null);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [campaignCode, setCampaignCode] = useState('');
  const [campaignPreviewLoading, setCampaignPreviewLoading] = useState(false);
  const [campaignPreviewMeta, setCampaignPreviewMeta] = useState(null);
  const pollRef = useRef(null);
  const pollStartAtRef = useRef(0);
  const pollWarnedRef = useRef(false);
  const pollTokenRef = useRef(0);
  const pollBaselineEndMsRef = useRef(0);

  const stopPolling = useCallback(() => {
    pollTokenRef.current += 1;
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollStartAtRef.current = 0;
    pollWarnedRef.current = false;
    pollBaselineEndMsRef.current = 0;
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      const data = await getSubscription();
      if (data?.status !== 'success') {
        throw new Error(resolveErrorMessage(data?.error_message, '获取订阅信息失败'));
      }
      setSubscription({ latest: data.latest || null, active: data.active || null });
      return data;
    } catch (e) {
      setError(e?.message || String(e));
      return null;
    }
  }, []);

  const refreshQuota = useCallback(async () => {
    try {
      const data = await getBillingQuota();
      if (data?.status !== 'success') {
        throw new Error(resolveErrorMessage(data?.error_message, '获取配额信息失败'));
      }
      setQuota(data);
      return data;
    } catch (e) {
      setError(e?.message || String(e));
      return null;
    }
  }, []);

  const loadCreditPacks = useCallback(async () => {
    setCreditPacksLoading(true);
    try {
      const resp = await getCreditPacks();
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '获取积分包失败'));
      }
      setCreditPacks(Array.isArray(resp?.items) ? resp.items : []);
      setCreditPackEligibility({
        eligible: Boolean(resp?.eligible),
        reason: String(resp?.eligibility?.reason || '').trim(),
        plan_id: String(resp?.eligibility?.plan_id || '').trim(),
        plan_name: String(resp?.eligibility?.plan_name || '').trim(),
      });
      return resp;
    } catch (_) {
      setCreditPacks([]);
      setCreditPackEligibility({
        eligible: false,
        reason: '',
        plan_id: '',
        plan_name: '',
      });
      return null;
    } finally {
      setCreditPacksLoading(false);
    }
  }, []);

  const loadCreditHistory = useCallback(async (page = 1) => {
    setCreditHistoryLoading(true);
    setCreditHistoryError('');
    try {
      const resp = await getCreditHistory({ page, page_size: CREDIT_HISTORY_PAGE_SIZE });
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '获取积分流水失败'));
      }
      setCreditHistoryItems(Array.isArray(resp?.items) ? resp.items : []);
      setCreditHistoryPage(Number(resp?.page || page));
      setCreditHistoryTotal(Number(resp?.total || 0));
      return resp;
    } catch (e) {
      setCreditHistoryItems([]);
      setCreditHistoryError(e?.message || String(e));
      return null;
    } finally {
      setCreditHistoryLoading(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const planResp = await getBillingPlans();
      if (planResp?.status !== 'success') {
        throw new Error(resolveErrorMessage(planResp?.error_message, '获取套餐失败'));
      }
      setPlans(planResp.plans || []);
      setTrialPromoEligibility(planResp?.trial_promo_eligibility || null);
      await Promise.all([
        refreshSubscription(),
        refreshQuota(),
        loadCreditPacks(),
        loadCreditHistory(1),
      ]);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [loadCreditHistory, loadCreditPacks, refreshQuota, refreshSubscription]);

  const startPolling = useCallback(({ orderNos = [], baselinePeriodEnd = '', orderType = 'subscription' } = {}) => {
    stopPolling();
    const pendingOrders = Array.from(new Set(
      (Array.isArray(orderNos) ? orderNos : [orderNos])
        .map((item) => String(item || '').trim())
        .filter(Boolean),
    ));
    if (pendingOrders.length === 0) return;
    const currentToken = pollTokenRef.current;
    pollBaselineEndMsRef.current = toUtcMs(baselinePeriodEnd);
    pollStartAtRef.current = Date.now();
    pollWarnedRef.current = false;

    const isPollingStale = () => currentToken !== pollTokenRef.current;
    const isSubscriptionUpgraded = (subData) => {
      const currentEndMs = toUtcMs(subData?.active?.current_period_end);
      if (!currentEndMs) return false;
      const baselineEndMs = pollBaselineEndMsRef.current;
      if (!baselineEndMs) return true;
      return currentEndMs > baselineEndMs;
    };

    pollRef.current = setInterval(async () => {
      let hasPaidSignal = false;
      let hasAppliedSignal = false;
      for (const outTradeNo of pendingOrders) {
        try {
          const reconcile = await reconcilePaymentStatus(outTradeNo);
          if (isPollingStale()) return;
          const tradeState = String(reconcile?.trade_state || '').toUpperCase();
          const payment = reconcile?.payment || {};
          if (isPaymentApplied(payment)) {
            hasAppliedSignal = true;
          }
          if (
            reconcile?.status === 'success'
            && ['SUCCESS', 'TRADE_SUCCESS', 'TRADE_FINISHED'].includes(tradeState)
          ) {
            hasPaidSignal = true;
          }
        } catch (_) {
          // ignore reconcile errors during polling
        }
      }

      if (hasAppliedSignal) {
        await Promise.all([refreshSubscription(), refreshQuota(), loadCreditPacks(), loadCreditHistory(1)]);
        if (isPollingStale()) return;
        message.success(orderType === 'credit_pack' ? '积分已到账' : '订阅已生效');
        setQrOpen(false);
        stopPolling();
        return;
      }

      const latest = await refreshSubscription();
      if (isPollingStale()) return;
      if (isSubscriptionUpgraded(latest)) {
        message.success('订阅已生效');
        setQrOpen(false);
        stopPolling();
        return;
      }
      if (hasPaidSignal) {
        await Promise.all([refreshQuota(), loadCreditHistory(creditHistoryPage)]);
      }

      if (!pollWarnedRef.current && pollStartAtRef.current > 0 && Date.now() - pollStartAtRef.current > 8 * 60 * 1000) {
        pollWarnedRef.current = true;
        message.warning('支付结果同步较慢，可点击“刷新订阅状态”或稍后重试。');
      }
    }, 5000);
  }, [creditHistoryPage, loadCreditHistory, loadCreditPacks, refreshQuota, refreshSubscription, stopPolling]);

  const handleRefreshSubscription = useCallback(async () => {
    try {
      const orderNos = Array.from(new Set(
        (Array.isArray(checkout?.order_nos) ? checkout.order_nos : [checkout?.out_trade_no])
          .map((item) => String(item || '').trim())
          .filter(Boolean),
      ));
      for (const outTradeNo of orderNos) {
        try {
          await reconcilePaymentStatus(outTradeNo);
        } catch (_) {
          // best-effort reconcile
        }
      }
    } finally {
      await Promise.all([refreshSubscription(), refreshQuota(), loadCreditPacks(), loadCreditHistory(creditHistoryPage)]);
    }
  }, [checkout?.order_nos, checkout?.out_trade_no, creditHistoryPage, loadCreditHistory, loadCreditPacks, refreshQuota, refreshSubscription]);

  const handleRedeem = useCallback(async () => {
    const code = String(redeemCode || '').trim();
    if (!code) {
      message.warning('请输入兑换码');
      return;
    }
    setRedeemLoading(true);
    setError('');
    try {
      const resp = await redeemSubscriptionCode({ code });
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '兑换失败'));
      }
      const periodEnd = formatDateTime(resp?.redeem?.period_end);
      const planName = String(resp?.redeem?.plan_name || '会员套餐');
      message.success(`兑换成功：${planName}${periodEnd ? `，有效期至 ${periodEnd}` : ''}`);
      setRedeemCode('');
      await Promise.all([refreshSubscription(), refreshQuota(), loadCreditPacks(), loadCreditHistory(1)]);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setRedeemLoading(false);
    }
  }, [loadCreditHistory, loadCreditPacks, redeemCode, refreshQuota, refreshSubscription]);

  const handlePreviewCampaign = useCallback(async () => {
    const campaignInput = String(campaignCode || '').trim();
    if (!campaignInput) {
      message.warning('请输入渠道码');
      return;
    }

    setCampaignPreviewLoading(true);
    try {
      const resp = await previewBillingPlans({ campaign_code: campaignInput });
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '折扣预览失败'));
      }
      setPlans(Array.isArray(resp?.plans) ? resp.plans : []);
      setTrialPromoEligibility(resp?.trial_promo_eligibility || null);
      const preview = resp?.preview || {};
      if (preview?.applied) {
        setCampaignPreviewMeta({
          applied: true,
          campaignName: String(preview?.campaign_name || '').trim(),
          codeSuffix: String(preview?.code_suffix || '').trim(),
          appliedPlanCount: Number(preview?.applied_plan_count || 0),
          maxDiscountCents: Number(preview?.max_discount_cents || 0),
        });
        const tip = `已应用渠道优惠，命中 ${Number(preview?.applied_plan_count || 0)} 个套餐`;
        message.success(tip);
      } else {
        setCampaignPreviewMeta(null);
        const tip = resolveErrorMessage(preview?.error_message, '该渠道码未命中当前套餐，价格未变化');
        message.info(tip);
      }
    } catch (e) {
      setCampaignPreviewMeta(null);
      const tip = e?.message || String(e);
      message.warning(tip);
    } finally {
      setCampaignPreviewLoading(false);
    }
  }, [campaignCode]);

  const handleResetCampaignPreview = useCallback(async () => {
    if (campaignPreviewLoading) return;
    setCampaignPreviewLoading(true);
    try {
      const resp = await previewBillingPlans({ campaign_code: '' });
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '恢复默认价格失败'));
      }
      setPlans(Array.isArray(resp?.plans) ? resp.plans : []);
      setTrialPromoEligibility(resp?.trial_promo_eligibility || null);
      setCampaignCode('');
      setCampaignPreviewMeta(null);
      message.success('已恢复默认价格');
    } catch (e) {
      message.warning(e?.message || String(e));
    } finally {
      setCampaignPreviewLoading(false);
    }
  }, [campaignPreviewLoading]);

  useEffect(() => {
    loadAll().catch((e) => setError(e?.message || String(e)));
    return () => stopPolling();
  }, [loadAll, stopPolling]);

  const handleScrollToCreditPacks = useCallback(() => {
    if (typeof document === 'undefined') return;
    const target = document.getElementById('billing-credit-packs');
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const activePlan = useMemo(() => {
    const active = subscription.active;
    if (!active) return null;
    const planId = String(active.plan_id || '').trim();
    return plans.find((plan) => String(plan.plan_id || '').trim() === planId) || null;
  }, [plans, subscription.active]);
  const canPurchaseCreditPacks = useMemo(() => {
    return Boolean(subscription?.active && creditPackEligibility?.eligible);
  }, [creditPackEligibility?.eligible, subscription?.active]);
  const creditPackBlockHint = useMemo(() => {
    if (!subscription?.active) {
      return '先开通月会员或年会员，再按需补充积分。';
    }
    if (String(creditPackEligibility?.reason || '').trim() === 'credit_pack_purchase_not_allowed') {
      const planName = String(creditPackEligibility?.plan_name || activePlan?.name || '当前套餐').trim();
      return `${planName}暂不支持购买积分包，请升级为月会员或年会员。`;
    }
    return '当前套餐暂不支持购买积分包。';
  }, [activePlan?.name, creditPackEligibility?.plan_name, creditPackEligibility?.reason, subscription?.active]);

  const planNameMap = useMemo(() => {
    const map = {};
    (plans || []).forEach((plan) => {
      const pid = String(plan?.plan_id || '').trim();
      if (pid) map[pid] = plan?.name || pid;
    });
    return map;
  }, [plans]);
  const orderedPlans = useMemo(() => {
    const list = Array.isArray(plans) ? [...plans] : [];
    return list.sort((a, b) => {
      const da = planDurationDays(a);
      const db = planDurationDays(b);
      if (da !== db) return da - db;
      return String(a?.plan_id || '').localeCompare(String(b?.plan_id || ''));
    });
  }, [plans]);
  const creditPackMonthlyUpgradePlan = useMemo(() => {
    return orderedPlans.find((plan) => (
      String(plan?.plan_id || '').trim() === 'creator_1m'
      && Boolean(plan?.benefits?.allow_credit_pack_purchase)
    )) || null;
  }, [orderedPlans]);
  const creditPackAnnualUpgradePlan = useMemo(() => {
    return orderedPlans.find((plan) => (
      String(plan?.plan_id || '').trim() === 'creator_1y'
      && Boolean(plan?.benefits?.allow_credit_pack_purchase)
    )) || null;
  }, [orderedPlans]);
  const creditPackMonthlyUpgradeLabel = useMemo(() => {
    const targetName = String(creditPackMonthlyUpgradePlan?.name || '创作者月会员').trim();
    return subscription?.active ? `升级到${targetName}` : `开通${targetName}`;
  }, [creditPackMonthlyUpgradePlan?.name, subscription?.active]);
  const creditPackAnnualUpgradeLabel = useMemo(() => {
    const targetName = String(creditPackAnnualUpgradePlan?.name || '创作者年会员').trim();
    return subscription?.active ? `升级到${targetName}` : `开通${targetName}`;
  }, [creditPackAnnualUpgradePlan?.name, subscription?.active]);

  const monthlyBasePriceCents = useMemo(() => {
    return resolveMonthlyBasePriceCents(plans);
  }, [plans]);
  const annualBasePriceCents = useMemo(() => {
    return resolveAnnualBasePriceCents(plans);
  }, [plans]);
  const historyColumns = useMemo(() => buildSubscriptionHistoryColumns({
    planNameMap,
    formatDateTime,
    formatPaymentAmount,
  }), [planNameMap]);
  const creditHistoryColumns = useMemo(() => buildCreditHistoryColumns({
    formatDateTime,
  }), []);

  const handleCheckout = async (plan) => {
    if (actionLoading) {
      message.info('正在创建支付订单，请稍候');
      return;
    }
    if (!String(plan?.plan_id || '').trim()) return;
    setActionLoading(true);
    setActionPlanId(String(plan?.plan_id || '').trim());
    setError('');
    try {
      const baselinePeriodEnd = String(subscription?.active?.current_period_end || '').trim();
      const result = await createSubscriptionCheckoutFlow({
        plan,
        campaignCode,
        createCheckout,
        resolveErrorMessage,
      });
      if (!result) return;
      const { checkout: nextCheckout, orderNos, channelErrors } = result;
      if (channelErrors.length > 0) {
        message.warning(`部分支付通道不可用：${channelErrors.join('；')}`);
      }
      setCheckout(nextCheckout);
      setQrOpen(true);
      startPolling({ orderNos, baselinePeriodEnd, orderType: 'subscription' });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setActionLoading(false);
      setActionPlanId('');
    }
  };

  const handleCreditPackCheckout = useCallback(async (pack) => {
    if (creditPackActionLoading) {
      message.info('正在创建支付订单，请稍候');
      return;
    }
    if (!canPurchaseCreditPacks) {
      message.warning(creditPackBlockHint);
      return;
    }
    if (!String(pack?.pack_id || '').trim()) return;
    setCreditPackActionLoading(true);
    setCreditPackActionPackId(String(pack?.pack_id || '').trim());
    setError('');
    try {
      const result = await createCreditPackCheckoutFlow({
        pack,
        createCreditPackCheckout,
        resolveErrorMessage,
      });
      if (!result) return;
      const { checkout: nextCheckout, orderNos, channelErrors } = result;
      if (channelErrors.length > 0) {
        message.warning(`部分支付通道不可用：${channelErrors.join('；')}`);
      }
      setCheckout(nextCheckout);
      setQrOpen(true);
      startPolling({ orderNos, orderType: 'credit_pack' });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setCreditPackActionLoading(false);
      setCreditPackActionPackId('');
    }
  }, [canPurchaseCreditPacks, creditPackActionLoading, creditPackBlockHint, startPolling]);

  const handleQrClose = () => {
    setQrOpen(false);
    stopPolling();
  };

  const currentStatus = subscription.active ? 'active' : subscription.latest ? 'expired' : 'none';
  const quotaRows = useMemo(() => {
    if (!quota?.plan) return [];
    const limits = quota?.plan?.limits || {};
    const usage = quota?.usage || {};
    const remaining = quota?.remaining || {};
    return [
      {
        key: 'accounts',
        label: '账号数',
        used: toNonNegativeInt(usage.accounts),
        limit: toNonNegativeInt(limits.max_accounts),
        remain: remaining.accounts,
      },
      {
        key: 'tasks',
        label: '任务数',
        used: toNonNegativeInt(usage.tasks),
        limit: toNonNegativeInt(limits.max_tasks),
        remain: remaining.tasks,
      },
      {
        key: 'monthly',
        label: '月发布',
        used: toNonNegativeInt(usage.monthly_publishes),
        limit: toNonNegativeInt(limits.monthly_publish_limit),
        remain: remaining.monthly_publishes,
      },
      {
        key: 'storage',
        label: '存储空间',
        usedText: formatStorage(usage.storage_bytes),
        limitText: formatStorage(limits.max_storage_bytes),
        remainText: remaining.storage_bytes === null || remaining.storage_bytes === undefined
          ? '不限'
          : formatStorage(remaining.storage_bytes),
      },
    ];
  }, [quota]);
  const creditSummary = quota?.credits || null;

  return (
    <div className="billing-page">
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <BillingSubscriptionSection
            currentStatus={currentStatus}
            activePlan={activePlan}
            subscription={subscription}
            formatDateTime={formatDateTime}
            quotaRows={quotaRows}
            creditSummary={creditSummary}
            toNonNegativeInt={toNonNegativeInt}
            onBuyCredits={handleScrollToCreditPacks}
            onRefreshSubscription={handleRefreshSubscription}
            onOpenHistory={openSubscriptionHistory}
          />

          <Col xs={24} xl={14}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <BillingPlanSection
                error={error}
                orderedPlans={orderedPlans}
                trialPromoEligibility={trialPromoEligibility}
                monthlyBasePriceCents={monthlyBasePriceCents}
                annualBasePriceCents={annualBasePriceCents}
                actionLoading={actionLoading}
                actionPlanId={actionPlanId}
                onCheckout={handleCheckout}
                redeemCode={redeemCode}
                onRedeemCodeChange={(value) => setRedeemCode(value)}
                redeemLoading={redeemLoading}
                onRedeem={handleRedeem}
                campaignCode={campaignCode}
                onCampaignCodeChange={(value) => {
                  setCampaignCode(value);
                  if (campaignPreviewMeta?.applied) {
                    setCampaignPreviewMeta(null);
                  }
                }}
                campaignPreviewMeta={campaignPreviewMeta}
                campaignPreviewLoading={campaignPreviewLoading}
                onPreviewCampaign={handlePreviewCampaign}
                onResetCampaignPreview={handleResetCampaignPreview}
                formatStorage={formatStorage}
                quotaTooltipMap={QUOTA_TOOLTIP_MAP}
                trialPromoReasonTextMap={TRIAL_PROMO_REASON_TEXT_MAP}
              />

              <BillingCreditPackSection
                canPurchaseCreditPacks={canPurchaseCreditPacks}
                creditPackBlockHint={creditPackBlockHint}
                creditPackAnnualUpgradePlan={creditPackAnnualUpgradePlan}
                creditPackMonthlyUpgradePlan={creditPackMonthlyUpgradePlan}
                creditPackAnnualUpgradeLabel={creditPackAnnualUpgradeLabel}
                creditPackMonthlyUpgradeLabel={creditPackMonthlyUpgradeLabel}
                actionLoading={actionLoading}
                actionPlanId={actionPlanId}
                onCheckout={handleCheckout}
                onScrollToCreditPacks={handleScrollToCreditPacks}
                onRefreshCreditPacks={() => loadCreditPacks()}
                creditPacksLoading={creditPacksLoading}
                creditPacks={creditPacks}
                toNonNegativeInt={toNonNegativeInt}
                formatCreditPackUnitPrice={formatCreditPackUnitPrice}
                creditPackActionLoading={creditPackActionLoading}
                creditPackActionPackId={creditPackActionPackId}
                onCreditPackCheckout={handleCreditPackCheckout}
              />
            </Space>
          </Col>
        </Row>

        <BillingCreditHistorySection
          creditHistoryError={creditHistoryError}
          creditHistoryPage={creditHistoryPage}
          creditHistoryLoading={creditHistoryLoading}
          creditHistoryItems={creditHistoryItems}
          creditHistoryColumns={creditHistoryColumns}
          creditHistoryTotal={creditHistoryTotal}
          pageSize={CREDIT_HISTORY_PAGE_SIZE}
          onRefresh={() => loadCreditHistory(creditHistoryPage)}
          onPageChange={(page) => loadCreditHistory(page)}
        />
      </Spin>

      <BillingHistoryDrawer
        open={historyOpen}
        onClose={closeSubscriptionHistory}
        onRefresh={() => loadSubscriptionHistory(historyPage)}
        historyLoading={historyLoading}
        historyError={historyError}
        historyItems={historyItems}
        historyColumns={historyColumns}
        historyPage={historyPage}
        historyTotal={historyTotal}
        pageSize={HISTORY_PAGE_SIZE}
        onPageChange={(page) => loadSubscriptionHistory(page)}
      />

      <CheckoutPaymentModal
        title="扫码支付"
        open={qrOpen}
        onCancel={handleQrClose}
        checkout={checkout}
        allPlans={plans}
        qrcodeSize={180}
      />
    </div>
  );
};

export default Billing;
